import type { ReactNode } from 'react';

/**
 * Semantic status chip — BRIDGE component, restyled to the Dispatch
 * Deck lamp-chip look (epic #835). Keeps the legacy `variant` API so
 * the ~50 unmigrated pages reskin without edits; new code uses
 * `src/ui/Chip.tsx`. Deleted with the bridge in #835 PR8.
 *
 * Uppercase + letter-spacing is gone: it is meaningless for CJK labels
 * and was the old system's loudest tic (DESIGN.md §3).
 */
type Variant = 'ok' | 'warning' | 'error' | 'neutral';

const VARIANT_CLASSES: Record<Variant, string> = {
  ok: 'border-ok-line bg-ok-dim/50 text-ok',
  warning: 'border-warn-line bg-warn-dim/50 text-warn',
  error: 'border-bad-line bg-bad-dim/50 text-bad',
  neutral: 'border-line bg-transparent text-paper-low',
};

const LAMP_CLASSES: Record<Variant, string> = {
  ok: 'bg-ok',
  warning: 'bg-warn',
  error: 'bg-bad',
  neutral: 'bg-line',
};

interface Props {
  readonly variant: Variant;
  readonly label: ReactNode;
  readonly testId?: string;
}

export function StatusChip({ variant, label, testId }: Props): JSX.Element {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-micro font-medium ${VARIANT_CLASSES[variant]}`}
    >
      <span aria-hidden="true" className={`inline-block h-1.5 w-1.5 shrink-0 rounded-[1.5px] ${LAMP_CLASSES[variant]}`} />
      {label}
    </span>
  );
}
